import { Head, Link, router } from '@inertiajs/react'
import { useState } from 'react'
import { toast } from 'sonner'
import { ArrowLeft, Plus, Trash2, Clock, CalendarX2, X, CalendarOff } from 'lucide-react'
import NavigationRail from '@/components/NavigationRail'
import SettingsNav from '@/components/SettingsNav'
import type { Agent } from '@/types/shared'

interface TimeSlot {
  id: string
  days: string
  startTime: string
  endTime: string
}

interface ScheduleException {
  id: string
  name: string
  type: 'closed' | 'custom'
  startDate: string
  endDate: string
  startTime?: string
  endTime?: string
  message?: string
}

interface BusinessHoursData {
  enabled: boolean
  timezone: string
  slots: TimeSlot[]
  exceptions: ScheduleException[]
}

interface BusinessHoursProps {
  businessHours: BusinessHoursData
  currentAgent: Agent
}

const DAY_OPTIONS = [
  { value: 'all_days', label: 'Todos os dias' },
  { value: 'weekdays', label: 'Dias da semana' },
  { value: 'weekends', label: 'Fins de semana' },
  { value: 'monday', label: 'Segunda-feira' },
  { value: 'tuesday', label: 'Terça-feira' },
  { value: 'wednesday', label: 'Quarta-feira' },
  { value: 'thursday', label: 'Quinta-feira' },
  { value: 'friday', label: 'Sexta-feira' },
  { value: 'saturday', label: 'Sábado' },
  { value: 'sunday', label: 'Domingo' },
]

// Generate time options in 15-minute increments
function generateTimeOptions(): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = []
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const value = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
      const period = hour < 12 ? 'am' : 'pm'
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
      const label = `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`
      options.push({ value, label })
    }
  }
  return options
}

const TIME_OPTIONS = generateTimeOptions()

function formatDateDisplay(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00')
  return date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short', year: 'numeric' })
}

function formatDateISO(date: Date): string {
  return date.toISOString().split('T')[0]
}

function getTodayISO(): string {
  return formatDateISO(new Date())
}

export default function BusinessHours({ businessHours, currentAgent }: BusinessHoursProps) {
  const [enabled, setEnabled] = useState(businessHours.enabled)
  const [slots, setSlots] = useState<TimeSlot[]>(businessHours.slots)
  const [exceptions, setExceptions] = useState<ScheduleException[]>(businessHours.exceptions || [])
  const [processing, setProcessing] = useState(false)

  // Exception modal state
  const [showExceptionModal, setShowExceptionModal] = useState(false)
  const [exceptionName, setExceptionName] = useState('')
  const [exceptionType, setExceptionType] = useState<'closed' | 'custom'>('closed')
  const [isDateRange, setIsDateRange] = useState(false)
  const [startDate, setStartDate] = useState(getTodayISO())
  const [endDate, setEndDate] = useState(getTodayISO())
  const [exceptionStartTime, setExceptionStartTime] = useState('09:00')
  const [exceptionEndTime, setExceptionEndTime] = useState('12:00')
  const [exceptionMessage, setExceptionMessage] = useState('')

  const addSlot = () => {
    const newSlot: TimeSlot = {
      id: crypto.randomUUID(),
      days: 'weekdays',
      startTime: '09:00',
      endTime: '18:00',
    }
    setSlots([...slots, newSlot])
  }

  const removeSlot = (id: string) => {
    setSlots(slots.filter(s => s.id !== id))
  }

  const updateSlot = (id: string, field: keyof TimeSlot, value: string) => {
    setSlots(slots.map(s => s.id === id ? { ...s, [field]: value } : s))
  }

  const resetExceptionModal = () => {
    setExceptionName('')
    setExceptionType('closed')
    setIsDateRange(false)
    setStartDate(getTodayISO())
    setEndDate(getTodayISO())
    setExceptionStartTime('09:00')
    setExceptionEndTime('12:00')
    setExceptionMessage('')
  }

  const openExceptionModal = () => {
    resetExceptionModal()
    setShowExceptionModal(true)
  }

  const closeExceptionModal = () => {
    setShowExceptionModal(false)
    resetExceptionModal()
  }

  const addException = () => {
    const newException: ScheduleException = {
      id: crypto.randomUUID(),
      name: exceptionName,
      type: exceptionType,
      startDate,
      endDate: isDateRange ? endDate : startDate,
      ...(exceptionType === 'custom' && {
        startTime: exceptionStartTime,
        endTime: exceptionEndTime,
      }),
      ...(exceptionMessage.trim() && { message: exceptionMessage.trim() }),
    }
    setExceptions([...exceptions, newException])
    closeExceptionModal()
  }

  const removeException = (id: string) => {
    setExceptions(exceptions.filter(e => e.id !== id))
  }

  const parseMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number)
    return hours * 60 + minutes
  }

  const validateException = (): string | null => {
    if (isDateRange && endDate < startDate) {
      return 'A data final deve ser posterior à data inicial'
    }
    if (exceptionType === 'custom' && parseMinutes(exceptionEndTime) <= parseMinutes(exceptionStartTime)) {
      return 'O horário final deve ser posterior ao inicial'
    }
    return null
  }

  const handleAddException = () => {
    const error = validateException()
    if (error) {
      toast.error(error)
      return
    }
    addException()
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setProcessing(true)

    router.patch('/settings/business-hours', {
      business_hours: {
        enabled,
        timezone: 'America/Sao_Paulo',
        slots: slots.map(s => ({
          id: s.id,
          days: s.days,
          start_time: s.startTime,
          end_time: s.endTime,
        })),
        exceptions: exceptions.map(e => ({
          id: e.id,
          name: e.name,
          type: e.type,
          start_date: e.startDate,
          end_date: e.endDate,
          start_time: e.startTime,
          end_time: e.endTime,
          message: e.message,
        })),
      },
    }, {
      preserveScroll: true,
      onSuccess: () => toast.success('Horário de funcionamento salvo'),
      onError: () => toast.error('Erro ao salvar configurações'),
      onFinish: () => setProcessing(false),
    })
  }

  return (
    <>
      <Head title="Horário de Funcionamento" />
      <div className="h-screen flex bg-gray-50">
        <NavigationRail currentAgent={currentAgent} activePage="settings" />

        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 bg-white border-b border-gray-200 flex items-center px-6">
            <Link
              href="/inbox"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm mr-4"
            >
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <h1 className="text-lg font-semibold text-gray-900">Configurações</h1>
          </header>

          <div className="flex-1 flex min-h-0">
            <SettingsNav activePage="business-hours" isAdmin={currentAgent.role === 'admin'} />

            <main className="flex-1 overflow-auto p-6">
              <div className="max-w-2xl">
                <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6">
                  {/* Enable toggle */}
                  <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-200">
                    <div>
                      <h2 className="text-sm font-medium text-gray-900">Horário de Funcionamento</h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Defina os horários em que sua equipe está disponível para atendimento.
                        Fora desses horários, os clientes verão uma mensagem informando quando você voltará.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setEnabled(!enabled)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors cursor-pointer ${
                        enabled ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Time slots */}
                  {enabled && (
                    <>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-medium text-gray-700">Horários</h3>
                          <button
                            type="button"
                            onClick={addSlot}
                            className="inline-flex items-center gap-1.5 text-sm text-orange-600 hover:text-orange-700 font-medium cursor-pointer"
                          >
                            <Plus className="w-4 h-4" />
                            Adicionar horário
                          </button>
                        </div>

                        {slots.length === 0 ? (
                          <div className="text-center py-8 text-gray-500 text-sm border border-dashed border-gray-200 rounded-lg">
                            <Clock className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                            <p>Nenhum horário definido</p>
                            <p className="text-xs text-gray-400 mt-1">
                              Sem horários definidos, o atendimento será considerado disponível 24h.
                            </p>
                            <button
                              type="button"
                              onClick={addSlot}
                              className="mt-3 text-orange-600 hover:text-orange-700 font-medium cursor-pointer"
                            >
                              Adicionar primeiro horário
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {slots.map((slot) => (
                              <div
                                key={slot.id}
                                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                              >
                                {/* Day selector */}
                                <select
                                  value={slot.days}
                                  onChange={(e) => updateSlot(slot.id, 'days', e.target.value)}
                                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                >
                                  {DAY_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>

                                {/* Start time */}
                                <select
                                  value={slot.startTime}
                                  onChange={(e) => updateSlot(slot.id, 'startTime', e.target.value)}
                                  className="w-28 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                >
                                  {TIME_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>

                                <span className="text-gray-400 text-sm">até</span>

                                {/* End time */}
                                <select
                                  value={slot.endTime}
                                  onChange={(e) => updateSlot(slot.id, 'endTime', e.target.value)}
                                  className="w-28 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                >
                                  {TIME_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>

                                {/* Delete button - only show if more than one slot */}
                                {slots.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => removeSlot(slot.id)}
                                    className="p-2 text-gray-400 hover:text-red-600 transition-colors cursor-pointer"
                                    title="Remover horário"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                                {slots.length === 1 && (
                                  <div className="w-8" /> // Spacer to maintain alignment
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Timezone notice */}
                        <p className="text-xs text-gray-500 mt-4">
                          Todos os horários estão no fuso horário de Brasília (GMT-3).
                        </p>
                      </div>

                      {/* Exceptions section */}
                      <div className="mt-8 pt-6 border-t border-gray-200">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <CalendarX2 className="w-4 h-4 text-gray-500" />
                            <h3 className="text-sm font-medium text-gray-700">Exceções de Horário</h3>
                          </div>
                          <button
                            type="button"
                            onClick={openExceptionModal}
                            className="inline-flex items-center gap-1.5 text-sm text-orange-600 hover:text-orange-700 font-medium cursor-pointer"
                          >
                            <Plus className="w-4 h-4" />
                            Adicionar exceção
                          </button>
                        </div>

                        <p className="text-sm text-gray-500 mb-4">
                          Configure feriados ou dias com horário especial. As exceções têm prioridade sobre os horários regulares.
                        </p>

                        {exceptions.length === 0 ? (
                          <div className="text-center py-8 text-gray-500 text-sm border border-dashed border-gray-200 rounded-lg">
                            <CalendarOff className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                            <p>Nenhuma exceção definida</p>
                            <p className="text-xs text-gray-400 mt-1">
                              Adicione feriados ou dias com horários especiais.
                            </p>
                            <button
                              type="button"
                              onClick={openExceptionModal}
                              className="mt-3 text-orange-600 hover:text-orange-700 font-medium cursor-pointer"
                            >
                              Adicionar primeira exceção
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {exceptions.map((exception) => (
                              <div
                                key={exception.id}
                                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                              >
                                <div className="flex items-center gap-3">
                                  {exception.type === 'closed' ? (
                                    <CalendarX2 className="w-4 h-4 text-red-500" />
                                  ) : (
                                    <Clock className="w-4 h-4 text-orange-500" />
                                  )}
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">
                                      {exception.name || 'Sem nome'}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {formatDateDisplay(exception.startDate)}
                                      {exception.endDate !== exception.startDate && (
                                        <> - {formatDateDisplay(exception.endDate)}</>
                                      )}
                                    </p>
                                    {exception.message && (
                                      <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[250px]">
                                        {exception.message}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-xs text-gray-500">
                                    {exception.type === 'closed' ? (
                                      'Fechado o dia todo'
                                    ) : (
                                      `${exception.startTime} - ${exception.endTime}`
                                    )}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => removeException(exception.id)}
                                    className="p-1.5 text-gray-400 hover:text-red-600 transition-colors cursor-pointer"
                                    title="Remover exceção"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {!enabled && (
                    <p className="text-sm text-gray-500">
                      Com o horário de funcionamento desativado, o atendimento será considerado disponível 24 horas por dia, 7 dias por semana.
                    </p>
                  )}

                  {/* Submit */}
                  <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                    <Link
                      href="/inbox"
                      className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancelar
                    </Link>
                    <button
                      type="submit"
                      disabled={processing}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      {processing ? 'Salvando...' : 'Salvar'}
                    </button>
                  </div>
                </form>
              </div>
            </main>
          </div>
        </div>
      </div>

      {/* Exception Modal */}
      {showExceptionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closeExceptionModal}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Adicionar Exceção</h3>
              <button
                type="button"
                onClick={closeExceptionModal}
                className="p-1 text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome (opcional)
                </label>
                <input
                  type="text"
                  value={exceptionName}
                  onChange={(e) => setExceptionName(e.target.value)}
                  placeholder="Ex: Natal, Carnaval..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mensagem para o cliente (opcional)
                </label>
                <textarea
                  value={exceptionMessage}
                  onChange={(e) => setExceptionMessage(e.target.value)}
                  placeholder="Ex: Estaremos de recesso de fim de ano. Voltaremos dia 02/01."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Essa mensagem será exibida no chat durante o período da exceção.
                </p>
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="exceptionType"
                      checked={exceptionType === 'closed'}
                      onChange={() => setExceptionType('closed')}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Fechado o dia todo</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="exceptionType"
                      checked={exceptionType === 'custom'}
                      onChange={() => setExceptionType('custom')}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Horário especial</span>
                  </label>
                </div>
              </div>

              {/* Date range toggle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data
                </label>
                <div className="flex items-center gap-4 mb-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="dateType"
                      checked={!isDateRange}
                      onChange={() => setIsDateRange(false)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Data única</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="dateType"
                      checked={isDateRange}
                      onChange={() => setIsDateRange(true)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Período</span>
                  </label>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  />
                  {isDateRange && (
                    <>
                      <span className="text-gray-400 text-sm">até</span>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        min={startDate}
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      />
                    </>
                  )}
                </div>
              </div>

              {/* Time (only for custom type) */}
              {exceptionType === 'custom' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Horário
                  </label>
                  <div className="flex items-center gap-3">
                    <select
                      value={exceptionStartTime}
                      onChange={(e) => setExceptionStartTime(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    >
                      {TIME_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <span className="text-gray-400 text-sm">até</span>
                    <select
                      value={exceptionEndTime}
                      onChange={(e) => setExceptionEndTime(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    >
                      {TIME_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
              <button
                type="button"
                onClick={closeExceptionModal}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleAddException}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
              >
                Adicionar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
